import { Injectable, inject } from '@angular/core';
import { Role } from '../../models/role.enum';
import { Router } from '@angular/router';
import { User, lesseeUserData } from '../../models/user.model';
import { LoginRequest } from '../../models/login-request.model';
import { BehaviorSubject, Observable, catchError, delay, map, of } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { NotificationService } from './notification.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private router = inject(Router);
  private currentUser: User | null = null;
  private currentLesseUser: lesseeUserData | null = null;
  private otpMessageSource = new BehaviorSubject<string | null>(null);

  private lesseeLoginStatusSubject = new BehaviorSubject<boolean>(false);
  lesseLoginStatus$ = this.lesseeLoginStatusSubject.asObservable();

  otpMessage$ = this.otpMessageSource.asObservable();

  private modalVisible = new BehaviorSubject<boolean>(false);
  modalVisible$ = this.modalVisible.asObservable();

  private loginType = new BehaviorSubject<string | null>(null);
  loginType$ = this.loginType.asObservable();

 private excelDataSource = new BehaviorSubject<any[]>([]);
  excelData$ = this.excelDataSource.asObservable();

  private apiUrl = environment.apiBaseUrl 
  constructor(private http: HttpClient, private notificationService: NotificationService) { }


  adminLogin(data: LoginRequest): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/authenticate`, data).pipe(
      map(response => {
        if (response.isSuccess) {
          this.currentUser = {
            email: data.email,
            role: Role.TtAdmin
          };
          localStorage.setItem('user', JSON.stringify(this.currentUser));

          this.otpMessageSource.next(response.message);
          return true;
        } else {
          this.notificationService.show('error', response.message);
          return false;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error?.error?.message || 'Bad request. Please check your input.';
        switch (error.status) {
          case 400:
            this.notificationService.show('error', errorMessage);
            break;
          case 401:
            this.notificationService.show('error', 'Unauthorized. Incorrect credentials.');
            break;
          case 404:
            this.notificationService.show('error', 'User not found.');
            break;
          case 0:
            this.notificationService.show('error', 'Network error. Please try again.');
            break;
          default:
            this.notificationService.show('error', 'Server error. Please try again later.');
        }        
        return of(false);
      })
    );
  }


  lessorLogin(data: LoginRequest): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/authenticate`, data).pipe(
      map(response => {
        if (response.isSuccess) {
          this.currentUser = {
            email: data.email,
            role: Role.LessorAdmin
          };
          localStorage.setItem('user', JSON.stringify(this.currentUser));

          this.otpMessageSource.next(response.message);
          return true;
        } else {
          return false;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error?.error?.message || 'Bad request. Please check your input.';
        switch (error.status) {
          case 400:
            this.notificationService.show('error', errorMessage);
            break;
          case 401:
            this.notificationService.show('error', 'Unauthorized. Incorrect credentials.');
            break;
          case 404:
            this.notificationService.show('error', 'User not found.');
            break;
          case 0:
            this.notificationService.show('error', 'Network error. Please try again.');
            break;
          default:
            this.notificationService.show('error', 'Server error. Please try again later.');
        }
        return of(false);
      })
    );
  }
  
   lesseeLogin(data: LoginRequest): Observable<boolean> {
    return this.http.post<any>(`${this.apiUrl}/authenticate`, data).pipe(
      map(response => {
        if (response.isSuccess) {
          this.currentUser = {
            email: data.email,
            role: Role.TtUser
          };
          localStorage.setItem('user', JSON.stringify(this.currentUser));

          this.otpMessageSource.next(response.message);
          return true;
        } else {
          return false;
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const errorMessage = error?.error?.message || 'Bad request. Please check your input.';
        switch (error.status) {
          case 400:
            this.notificationService.show('error', errorMessage);
            break;
          case 401:
            this.notificationService.show('error', 'Unauthorized. Incorrect credentials.');
            break;
          case 404:
            this.notificationService.show('error', 'User not found.');
            break;
          case 0:
            this.notificationService.show('error', 'Network error. Please try again.');
            break;
          default:
            this.notificationService.show('error', 'Server error. Please try again later.');
        }
        return of(false);
      })
    );
  }

logout(userType: 'lessor' | 'lessee' | 'admin') {
  const clearSession = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    this.currentUser = null;
    if (userType === 'lessor') {
      this.router.navigate(['/lessor-login']);
    } else if (userType === 'lessee') {
      this.router.navigate(['/lessee-login']);
    }
      else if (userType === 'admin') {
      this.router.navigate(['/admin-login']);
    }
  };

  const logoutUrl = `${this.apiUrl}/logout-user`;
  this.http.post(logoutUrl, {}).subscribe({
    next: clearSession,
    error: clearSession
  });
}

 resetPassword(newPassword: string): Observable<any> {
  const payload = {
    newPassword: newPassword
  };
  return this.http.post(`${this.apiUrl}/reset-password`, payload);
}

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    const url = `${this.apiUrl}/change-password`;
    const payload = {
      currentPassword: currentPassword.trim(),
      newPassword: newPassword.trim()
    };
    return this.http.post(url, payload);
  }
  validateOtp(email: string, otp: string): Observable<User> {
    return this.http.get<any>(
      `${this.apiUrl}/validateOtp?userEmail=${encodeURIComponent(email)}&otp=${otp}`
    ).pipe(
      map(response => {
        if (response.isSuccess && response.data?.accessToken) {
          const data = response.data;

          const roles: string[] = data.roles || [];

          const rolePriority: Role[] = [
            Role.TtAdmin,
            Role.TtProxyAdmin,
            Role.TtAgent,
            Role.TtUser,
            Role.LessorAdmin,
            Role.LessorProxyAdmin,
            Role.LessorAgent,
            Role.Lessee
          ];

          const assignedRole = rolePriority.find(r => roles.includes(r));

          if (!assignedRole) {
            this.notificationService.show('error', 'No valid role assigned. Please contact admin.');
            throw new Error('No valid role found for the user.');
          }
          const user: User = {
            email: data.emailId,
            role: assignedRole,
            permissions: data?.permissions ?? ['TtRead', 'TtCreate', 'TtUpdate','TtDelete'],
          };


          localStorage.setItem('user', JSON.stringify(user));
          localStorage.setItem('token', data.accessToken);
          localStorage.setItem('refreshToken', data.refreshToken);
          localStorage.setItem('isDefaultPasswordUsed', data.isDefaultPasswordUsed);

          this.currentUser = user;

          return user;
        } else {
          throw new Error('Invalid OTP response');
        }
      })
    );
  }

indivisualLeseeRegister(payload: any): Observable<any> {
  return this.http.post(`${this.apiUrl}/IndividualLessee/register-user`, payload);
}
  sendPasswordResetLink(email: string) {
    const url = `${this.apiUrl}/forgot-password?email=${encodeURIComponent(email)}`;
    return this.http.post(url, {});
  }
  getUser(): User | null {
    if (this.currentUser) return this.currentUser;

    const storedUser = localStorage.getItem('user');

    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);

        const isValidRole = Object.values(Role).includes(parsed.role);
        const hasPermissions = Array.isArray(parsed.permissions);

        if (parsed.email && isValidRole && hasPermissions) {
          this.currentUser = {
            email: parsed.email,
            role: parsed.role as Role,
            token: parsed.token ?? '', 
            permissions: parsed.permissions
          };

          return this.currentUser;
        } else {
          this.notificationService.show('error', 'Invalid session data. Please login again.');
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      } catch (e) {
        localStorage.removeItem('user');
      }
    }

    return null;
  }


  lesseLoginShow(type: string) {
    this.loginType.next(type);
    this.modalVisible.next(true);
  }

  lesseeLoginHide() {
    this.modalVisible.next(false);
    this.loginType.next(null);
  }

  getlesseeUser(): lesseeUserData | null {
    if (this.currentLesseUser) return this.currentLesseUser;
    const stored = localStorage.getItem('user');
    if (stored) {
      this.currentLesseUser = JSON.parse(stored);
      return this.currentLesseUser;
    }
    return null;
  }

  getRole(): Role | null {
    const user = this.getUser();
    return user?.role ?? null;
  }

  isAuthenticated(): boolean {
    const token = localStorage.getItem('token');
    return !!token && !!this.getUser();
  }

  getPermissions(): string[] {
    return this.currentUser?.permissions ?? [];
  }

  hasPermission(permission: string): boolean {
    return this.currentUser?.permissions?.includes(permission) ?? false;
  }

  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(p => this.hasPermission(p));
  }

  hasAllPermissions(permissions: string[]): boolean {
    return permissions.every(p => this.hasPermission(p));
  }
  
  setLesseeLoginStatus(status: boolean) {
    this.lesseeLoginStatusSubject.next(status);
  }
  setExcelData(data: any[]) {
    this.excelDataSource.next(data);
  }

  getExcelData(): any[] {
    return this.excelDataSource.getValue();
  }

}
